<template>
  <SettingsCard
    :title="$t('settings.company.colors.title')"
    :subtitle="$t('settings.company.colors.subtitle')"
    icon="mdi-palette"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="py-4 px-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <div class="d-flex align-center">
              <span class="font-weight-bold">
                {{ $t('settings.company.colors.body-texts.0') }}
              </span>
            </div>
            <span class="text-grey">
              {{ $t('settings.company.colors.body-texts.1') }}
            </span>
          </div>
        </v-col>
        <v-col
          v-if="primaryColor"
          cols="12"
          sm="6"
          class="d-flex flex-column justify-center px-0 px-md-2 mt-4"
        >
          <div class="d-flex justify-center align-end">
            <ContrastIndicator
              :selected-color="primaryColor"
              :has-sufficient-contrast="hasPrimarySufficientContrast"
            />
            <v-btn
              color="primary"
              class="text-white"
              icon="mdi-pencil"
              size="small"
              :disabled="showUpselling"
              @click="triggerPrimaryDialog"
            />
          </div>
          <v-alert
            v-if="!hasPrimarySufficientContrast"
            class="mt-2"
            type="info"
            text
          >
            {{ $t('alerts.color-dialog.low-contrast-hint') }}
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <div class="d-flex align-center">
              <span class="font-weight-bold">
                {{ $t('settings.company.colors.body-texts.2') }}
              </span>
            </div>
            <span class="text-grey">
              {{ $t('settings.company.colors.body-texts.3') }}
            </span>
          </div>
        </v-col>
        <v-col
          v-if="secondaryColor"
          cols="12"
          sm="6"
          class="d-flex flex-column justify-center px-0 px-md-2 mt-4"
        >
          <div class="d-flex justify-center align-end">
            <ContrastIndicator
              :selected-color="secondaryColor"
              :has-sufficient-contrast="hasSecondarySufficientContrast"
            />
            <v-btn
              color="primary"
              class="text-white"
              icon="mdi-pencil"
              :disabled="showUpselling"
              size="small"
              @click="triggerSecondaryDialog"
            />
          </div>
          <v-alert
            v-if="!hasSecondarySufficientContrast"
            class="mt-2"
            type="info"
            text
          >
            {{ $t('alerts.color-dialog.low-contrast-hint') }}
          </v-alert>
        </v-col>
      </v-row>
    </div>

    <SetColorDialog
      v-model="primaryColor"
      :title="$t('settings.company.colors.adjust-primary-color')"
      :abort="resetPrimaryColor"
      :save="updateColors"
      :show-dialog="showPrimaryDialog"
      @update:show-dialog="showPrimaryDialog = $event"
    />

    <SetColorDialog
      v-model="secondaryColor"
      :title="$t('settings.company.colors.adjust-secondary-color')"
      :abort="resetSecondaryColor"
      :save="updateColors"
      :show-dialog="showSecondaryDialog"
      @update:show-dialog="showSecondaryDialog = $event"
    />
    <BlurredBackdropUpselling v-if="showUpselling" />
  </SettingsCard>
</template>

<script>
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import { meetsAA } from '@/lib/colorContrastRatio.js'
import bus, { eventNames } from '@/lib/eventBus'
import PENDING_CHANGES_FOR_REGENERATION from '@/queries/PendingChangesForRegeneration.gql'

import ContrastIndicator from '../../../components/ContrastIndicator.vue'
import SetColorDialog from '../../../components/SetColorDialog.vue'
import COMPANY from './queries/CompanyData.gql'
import UPDATE_COMPANY_THEME from './queries/UpdateCompanyTheme.gql'
export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'colors' })
    return {
      isOpen,
      setOpen,
    }
  },
  name: 'ColorSettings',
  components: {
    SettingsCard,
    ContrastIndicator,
    SetColorDialog,
    BlurredBackdropUpselling,
  },
  props: {
    company: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    showUpselling: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPrimaryDialog: false,
      showSecondaryDialog: false,
      maxBrightness: 186,
      localPrimaryColor: null,
      localSecondaryColor: null,
    }
  },
  computed: {
    primaryColor: {
      get() {
        return this.localPrimaryColor
      },
      set(value) {
        this.localPrimaryColor = value
      },
    },
    secondaryColor: {
      get() {
        return this.localSecondaryColor
      },
      set(value) {
        this.localSecondaryColor = value
      },
    },
    hasPrimarySufficientContrast() {
      return meetsAA(this.primaryColor)
    },
    hasSecondarySufficientContrast() {
      return meetsAA(this.secondaryColor)
    },
  },
  watch: {
    'company.theme.colors': {
      immediate: true,
      handler(newColors) {
        if (newColors) {
          this.localPrimaryColor = newColors.primary
          this.localSecondaryColor = newColors.secondary
        }
      },
    },
  },
  methods: {
    triggerPrimaryDialog() {
      this.$tracking.event('Settings', 'Clicked', 'Adjust Primary Color')
      this.showPrimaryDialog = true
      this.showSecondaryDialog = false
    },
    triggerSecondaryDialog() {
      this.$tracking.event('Settings', 'Clicked', 'Adjust Secondary Color')
      this.showSecondaryDialog = true
      this.showPrimaryDialog = false
    },
    resetPrimaryColor() {
      this.$tracking.event('Settings', 'Clicked', 'Reset Primary Color')
      this.showPrimaryDialog = false
      this.localPrimaryColor = this.company.theme.colors.primary
    },
    resetSecondaryColor() {
      this.$tracking.event('Settings', 'Clicked', 'Reset Secondary Color')
      this.showSecondaryDialog = false
      this.localSecondaryColor = this.company.theme.colors.secondary
    },
    async updateColors() {
      this.showPrimaryDialog = false
      this.showSecondaryDialog = false

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_THEME,
          variables: {
            input: {
              companyId: this.company.id,
              colors: {
                primary: this.primaryColor,
                secondary: this.secondaryColor,
              },
            },
          },
          refetchQueries: [
            { query: COMPANY, variables: { id: this.company.id } },
            { query: PENDING_CHANGES_FOR_REGENERATION, variables: { companyId: this.company.id } },
          ],
        })
        this.$tracking.event('Settings', 'Edited', 'Update Company Theme')
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.company.success'),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.company.error'),
        })
      }
    },
  },
}
</script>
<style>
.mdi-unfold-more-horizontal {
  font-size: 1.5rem;
}
</style>
